import { useState } from 'react';
import { Upload } from 'lucide-react';
import type { FormatKind, ComplianceReportDetailDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useFinanceFormats } from '@/hooks/queries/useFinanceFormats';
import { answered } from '@/lib/reportsRead';
import { unreadyMessage } from '@/lib/financeRemainderReads';
import { skip } from '@signalvote/data';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { FormatPicker } from './FormatPicker';
import { amendmentWorkflowHint } from './amendmentWorkflowHint';
import { FilePreflightModal } from './FilePreflightModal';
import type { AmendmentModel } from '@signalvote/shared';

interface FilePackageButtonProps {
  report: ComplianceReportDetailDto;
  /**
   * Campaign's resolved state code. ADR-022 made `rulesetCode` the sole
   * jurisdiction source of truth (the redundant `defaultFormatStateCode` is
   * gone), so this resolves from the report's stateCode / settings.rulesetCode.
   * Null = not yet configured, in which case the button is disabled with a
   * guidance message.
   */
  stateCode: string | null;
  /**
   * #482 — federal download-and-self-upload: this button is the GENERATE step (the `.fec` embeds
   * the treasurer's certification, so the attestation review still runs), but generating does NOT
   * file — the report stays Ready and the treasurer uploads the file to efo.fec.gov, then marks it
   * filed. When true the control reads "Generate & download report" (not "File Report") and the
   * review modal's action reads "Certify and download". Default false (in-system e-file = file).
   */
  nonFinalizing?: boolean;
  /**
   * §E — visual prominence of the File Report button. For an append-only
   * (PortalOnly / Alabama) jurisdiction the recommended path is Export, so the
   * caller demotes File to `'secondary'` (outline). Federal in-system e-file
   * keeps the default `'primary'` (filled green). Only prominence changes — the
   * button does exactly the same thing either way.
   */
  variant?: 'primary' | 'secondary';
}

/**
 * FIN-011 — combines (optional) FormatPicker + File Report button.
 * PPR-009 — clicking File opens the {@link FilePreflightModal} review modal
 * instead of firing the submission directly. The modal owns the actual POST
 * to `/file`; this component owns the format picker (when multiple formats
 * are registered) and the post-file amendment-workflow hint.
 */
export function FilePackageButton({
  report,
  stateCode,
  variant = 'primary',
  nonFinalizing = false,
}: FilePackageButtonProps) {
  const formatsRead = useFinanceFormats(stateCode || skip);
  const formats = answered(formatsRead);
  const formatsUnready = unreadyMessage(formatsRead, 'the file formats');
  // The served email-transport verdict (NC → NCSBE). When set, the post-file amendment hint says
  // "email the corrected file to NCSBE" instead of the SelfContainedUpload "upload the new package"
  // wording — driven by the declared transport, never a `stateCode === 'NC'` boolean.
  const { isEmailFiling, portalName } = usePortalUploadModel();

  // User's explicit pick (when multi-format). Null means "default to the
  // first registered format" — derived below rather than stored, to avoid
  // a render-then-effect-then-render seeding pass.
  const [pickedFormat, setPickedFormat] = useState<FormatKind | null>(null);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [lastModel, setLastModel] = useState<AmendmentModel | null>(null);

  // Resolved value the picker displays: explicit user choice if any,
  // otherwise the first registered format.
  const resolvedFormat: FormatKind | null = pickedFormat ?? formats?.[0] ?? null;
  const isMultiFormat = (formats?.length ?? 0) > 1;

  if (formatsRead.status === 'loading') {
    return <p className="text-sm text-muted-foreground">Loading file formats...</p>;
  }

  if (formatsUnready) {
    return (
      <p role="alert" className="text-sm text-destructive" data-testid="formats-unready-message">
        {formatsUnready}
      </p>
    );
  }

  if (!stateCode || !formats || formats.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="no-formats-message">
        No electronic submission format is registered for this campaign's state yet.
      </p>
    );
  }

  // Pass formatOverride to the modal only when a multi-format picker exists
  // — single-format states (AL today) post without one so the endpoint
  // falls through to the campaign's DefaultFormatKind.
  const overrideForModal = isMultiFormat ? resolvedFormat : null;

  return (
    <div className="space-y-3">
      {isMultiFormat && (
        <FormatPicker
          formats={formats}
          value={resolvedFormat}
          onChange={setPickedFormat}
        />
      )}
      <Button
        variant={variant === 'secondary' ? 'outline' : 'default'}
        onClick={() => setPreflightOpen(true)}
        data-testid="file-report-button"
      >
        <Upload className="mr-1 h-4 w-4" />
        {nonFinalizing ? 'Generate & download report' : 'File Report'}
      </Button>
      {lastModel && (
        <div
          className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900"
          data-testid="amendment-workflow-hint"
        >
          {amendmentWorkflowHint(lastModel, stateCode, isEmailFiling, portalName)}
        </div>
      )}
      <FilePreflightModal
        open={preflightOpen}
        reportId={report.id}
        concurrencyVersion={report.concurrencyVersion}
        formatOverride={overrideForModal}
        nonFinalizing={nonFinalizing}
        onClose={() => setPreflightOpen(false)}
        onFiled={(model) => {
          setPreflightOpen(false);
          if (model) setLastModel(model);
        }}
      />
    </div>
  );
}
